/**
 * Utilities for generating user initials from names
 */

/**
 * Generates initials from a user name
 *
 * Rules:
 * - Full name (e.g., "João Silva") → "JS" (first letter of first + first letter of last)
 * - Single name (e.g., "João") → "JO" (first two letters)
 * - Empty/invalid name → "??" (fallback)
 *
 * @param name - User's full name
 * @returns Two-character uppercase initials
 */
export function generateInitials(name: string | null | undefined): string {
  if (!name || name.trim() === '') {
    return '??';
  }

  const cleaned = name.trim();
  const words = cleaned.split(/\s+/).filter(w => w.length > 0);

  if (words.length >= 2) {
    // First letter of first name + first letter of last name
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  } else if (words.length === 1 && words[0].length >= 2) {
    // First two letters
    return words[0].substring(0, 2).toUpperCase();
  } else if (words.length === 1) {
    // Single letter, repeated
    return (words[0][0] + words[0][0]).toUpperCase();
  }

  return '??';
}

/**
 * Generates a consistent background color from a string
 * Uses a hash of the string to select from a predefined color palette
 *
 * @param str - Input string (typically a name)
 * @returns Hex color code
 */
export function getColorFromString(str: string | null | undefined): string {
  // Paleta de cores mais suaves (Softer/Muted Colors) para melhor estética
  const colors = [
    '#5C7285', // Slate Blue
    '#7D6E83', // Muted Purple
    '#819CA5', // Steel Blue
    '#A68A64', // Earthy Gold
    '#9B6A6C', // Dusty Rose
    '#6B8067', // Sage Green
    '#647E8B', // Blue Gray
    '#8B7E74', // Taupe
  ];

  if (!str || str.trim() === '') {
    return colors[0]; // Default to primary color
  }

  // Simple hash function
  const hash = str.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  return colors[Math.abs(hash) % colors.length];
}
